import { useEffect, useState } from "react";
import {
  Button,
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Snackbar,
  CircularProgress,
  Divider
} from "@mui/material";
import { getSocialMediaStatus, connectMeta, disconnectMeta, revokeMetaAccess } from "../../api/meta";
import { useAuth } from "../../context/AuthContext";

type SocialMediaSettingsProps = {
  facebookConnected: boolean;
  instagramConnected: boolean;
  setFacebookConnected: React.Dispatch<React.SetStateAction<boolean>>;
  setInstagramConnected: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
};

const SocialMediaSettings = ({
  facebookConnected,
  instagramConnected,
  setFacebookConnected,
  setInstagramConnected,
  setErrorMessage,
}: SocialMediaSettingsProps) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [facebookInfo, setFacebookInfo] = useState<null | {
    name: string;
    followers: number;
    category: string;
  }>(null);

  const [instagramInfo, setInstagramInfo] = useState<null | {
    username: string;
    followers: number;
    media_count: number;
  }>(null);

  const [showResetMessage, setShowResetMessage] = useState(false);
  const [loading, setLoading] = useState<"facebook" | "instagram" | "reset" | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading("reset");
      const data = await getSocialMediaStatus();
      if (data) {
        setFacebookConnected(data.facebook_connected);
        setInstagramConnected(data.instagram_connected);
        if (data.facebook_page) setFacebookInfo(data.facebook_page);
        if (data.instagram_account) setInstagramInfo(data.instagram_account);
      }
      setLoading(null);
    };
    fetchStatus();
  }, []);

  const handleConnectMeta = async (provider: "facebook" | "instagram") => {
    setErrorMessage(null);
    setLoading(provider);
    try {
      await connectMeta(provider);
    } catch (err: any) {
      console.error("❌ Meta connection error:", err);
      if (err?.response?.status === 409) {
        setErrorMessage(`This ${provider} account is already connected to another business.`);
      } else if (err?.message) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage(`Failed to connect ${provider}. Please try again.`);
      }
    } finally {
      setLoading(null);
    }
  };

  const handleDisconnectMeta = async (provider: "facebook" | "instagram") => {
    setLoading(provider);
    try {
      await disconnectMeta(provider);
      if (provider === "facebook") {
        setFacebookConnected(false);
        setFacebookInfo(null);
      }
      if (provider === "instagram") {
        setInstagramConnected(false);
        setInstagramInfo(null);
      }
    } catch (err) {
      console.error(`❌ Error disconnecting ${provider}:`, err);
    } finally {
      setLoading(null);
    }
  };

  const isLoading = (key: string) => loading === key;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Social Media Connections
      </Typography>

      {/* ✅ Facebook Section */}
      <Box
        sx={{
          display: isMobile ? "block" : "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 4,
        }}
      >
        <Button
          onClick={() =>
            facebookConnected
              ? handleDisconnectMeta("facebook")
              : handleConnectMeta("facebook")
          }
          variant="contained"
          color="secondary"
          disabled={isLoading("facebook")}
          startIcon={isLoading("facebook") ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {facebookConnected ? "Disconnect Facebook" : "Connect Facebook"}
        </Button>

        {facebookConnected && facebookInfo && (
          <Paper elevation={2} sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{facebookInfo.name}</strong>
            </Typography>
            <Typography variant="body2">
              <strong>Followers:</strong> {facebookInfo.followers?.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              <strong>Category:</strong> {facebookInfo.category}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ✅ Instagram Section */}
      <Box
        sx={{
          display: isMobile ? "block" : "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Button
          onClick={() =>
            instagramConnected
              ? handleDisconnectMeta("instagram")
              : handleConnectMeta("instagram")
          }
          variant="contained"
          color="secondary"
          disabled={isLoading("instagram")}
          startIcon={isLoading("instagram") ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {instagramConnected ? "Disconnect Instagram" : "Connect Instagram"}
        </Button>

        {instagramConnected && instagramInfo && (
          <Paper elevation={2} sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>@{instagramInfo.username}</strong>
            </Typography>
            <Typography variant="body2">
              <strong>Followers:</strong> {instagramInfo.followers}
            </Typography>
            <Typography variant="body2">
              <strong>Posts:</strong> {instagramInfo.media_count}
            </Typography>
          </Paper>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* ✅ Reset Meta */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Need to reset & revoke your Meta connections?
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={async () => {
            setLoading("reset");
            try {
              await revokeMetaAccess();
              setFacebookConnected(false);
              setInstagramConnected(false);
              setFacebookInfo(null);
              setInstagramInfo(null);
              setShowResetMessage(true);
            } catch (err) {
              console.error("❌ Failed to reset Meta:", err);
              setErrorMessage("Something went wrong. Please try again.");
            } finally {
              setLoading(null);
            }
          }}
          startIcon={isLoading("reset") ? <CircularProgress size={20} color="inherit" /> : null}
          disabled={isLoading("reset")}
        >
          Reset Meta Connections
        </Button>
      </Box>

      {/* ✅ Snackbar message */}
      <Snackbar
        open={showResetMessage}
        autoHideDuration={4000}
        onClose={() => setShowResetMessage(false)}
        message="Meta connections successfully reset"
      />
    </Box>
  );
};

export default SocialMediaSettings;
